import { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { Menu, X, Search as SearchIcon, ChevronDown } from "lucide-react";
import Brand from "@/components/site/Brand";
import { getServices, getIndustries } from "@/lib/api";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", mega: "services" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
  { label: "Resources", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

const PILLAR_ORDER = ["Consult", "Implement", "Manage", "Test"];

export default function Header({ onOpenPalette }) {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(null);
  const [services, setServices] = useState([]);
  const [industries, setIndustries] = useState([]);
  const reduced = useReducedMotion();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    getServices().then(setServices).catch(() => {});
    getIndustries().then(setIndustries).catch(() => {});
  }, []);

  // Data-driven pillars grouping
  const pillars = useMemo(() => {
    const byPillar = { Consult: [], Implement: [], Manage: [], Test: [] };
    for (const s of services) {
      if (s.pillar && byPillar[s.pillar]) byPillar[s.pillar].push(s);
    }
    for (const k of Object.keys(byPillar)) {
      byPillar[k].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return byPillar;
  }, [services]);

  return (
    <motion.header
      data-testid="site-header"
      initial={reduced ? false : { y: -18, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={[
        "fixed top-0 left-0 right-0 z-50 bg-white border-b transition-shadow duration-300",
        scrolled
          ? "shadow-[0_2px_8px_rgba(11,11,16,0.06)] border-[rgba(11,11,16,0.08)]"
          : "border-[rgba(11,11,16,0.04)]",
      ].join(" ")}
      onMouseLeave={() => setOpenMenu(null)}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-10 h-[60px] md:h-[72px] flex items-center justify-between">
        <Brand
          variant="full"
          height={44}
          imgClassName="h-[32px] md:h-[42px]"
          className="py-2"
          light
        />

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setOpenMenu(item.mega || null)}
            >
              <NavLink
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  [
                    "relative font-body text-[14px] py-2 transition-colors duration-300",
                    isActive
                      ? "text-[#0B0B10]"
                      : "text-[#0B0B10]/85 hover:text-[#5B2A86]",
                  ].join(" ")
                }
                end={item.to === "/"}
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={[
                        "absolute -bottom-0.5 left-0 h-[2px] bg-[#F5B841] transition-[width] duration-300",
                        isActive ? "w-full" : "w-0",
                      ].join(" ")}
                    />
                  </>
                )}
              </NavLink>
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            data-testid="header-search-open"
            onClick={onOpenPalette}
            aria-label="Open search"
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-[rgba(11,11,16,0.12)] hover:border-[#5B2A86] text-[#0B0B10]/70 hover:text-[#5B2A86] px-3 py-1.5 text-xs transition-colors"
          >
            <SearchIcon className="h-3.5 w-3.5" />
            <span className="font-editorial-mono tracking-[0.18em] text-[10.5px]">SEARCH</span>
            <kbd className="hidden xl:inline-flex items-center rounded border border-[rgba(11,11,16,0.10)] bg-white px-1.5 py-0.5 text-[10px] text-[#0B0B10]/60">
              ⌘K
            </kbd>
          </button>
          {/* Book a Consultation — solid purple with WHITE Inter Tight text */}
          <Link
            to="/contact"
            data-testid="header-cta-book"
            aria-label="Book a Consultation"
            style={{
              backgroundColor: "#5B2A86",
              color: "#FFFFFF",
              fontFamily: '"Inter Tight", Inter, system-ui, sans-serif',
              fontWeight: 500,
              letterSpacing: "0.02em",
              minHeight: 44,
            }}
            className="inline-flex items-center justify-center rounded-md px-4 md:px-5 py-2 md:py-2.5 text-[13px] md:text-[14px] transition-all hover:scale-[1.02] hover:bg-[#4A2270]"
          >
            Book a Consultation
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="lg:hidden ml-1 inline-flex items-center justify-center h-11 w-11 rounded-full text-[#0B0B10]"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mega menu — SERVICES (editorial white) — data-driven by pillar */}
      <AnimatePresence>
        {openMenu === "services" && services.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute left-0 right-0 top-full bg-white border-b border-[rgba(11,11,16,0.08)] shadow-[0_18px_44px_-18px_rgba(0,0,0,0.10)]"
            onMouseEnter={() => setOpenMenu("services")}
          >
            <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-8 grid grid-cols-4 gap-8">
              {PILLAR_ORDER.map((pillar) => (
                <div key={pillar}>
                  <div className="font-editorial-mono text-[10.5px] text-[#5B2A86] mb-4">
                    {pillar.toUpperCase()}
                  </div>
                  <ul className="space-y-2.5">
                    {pillars[pillar].map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={`/services/${s.slug}`}
                          className="group inline-flex items-baseline gap-1.5 text-[13.5px] text-[#0B0B10] hover:text-[#5B2A86] transition-colors"
                          data-testid={`megamenu-service-${s.slug}`}
                        >
                          <span className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#F5B841]">→</span>
                          <span>{s.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile drawer — editorial white with accordion */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-[rgba(11,11,16,0.08)] bg-white overflow-hidden"
          >
            <div className="mx-auto max-w-[1440px] px-6 py-4 space-y-1">
              {NAV.map((item) => (
                <div key={item.label} className="border-b border-[rgba(11,11,16,0.06)]">
                  {item.mega === "services" ? (
                    <>
                      <button
                        onClick={() => setMobileSubOpen(mobileSubOpen === "services" ? null : "services")}
                        className="w-full flex items-center justify-between py-3 text-[15px] text-[#0B0B10]"
                        data-testid="mobile-services-toggle"
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${mobileSubOpen === "services" ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileSubOpen === "services" && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-2"
                          >
                            {PILLAR_ORDER.map((p) => (
                              <div key={p} className="pb-3">
                                <div className="font-editorial-mono text-[10px] text-[#5B2A86] mt-3 mb-2">{p.toUpperCase()}</div>
                                <ul>
                                  {pillars[p].map((s) => (
                                    <li key={s.slug}>
                                      <Link
                                        to={`/services/${s.slug}`}
                                        onClick={() => setMobileOpen(false)}
                                        className="block py-2 text-[14px] text-[#0B0B10] hover:text-[#5B2A86]"
                                        data-testid={`mobile-svc-${s.slug}`}
                                      >
                                        {s.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <NavLink
                      to={item.to}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        [
                          "block py-3 text-[15px]",
                          isActive ? "text-[#5B2A86]" : "text-[#0B0B10]",
                        ].join(" ")
                      }
                      end={item.to === "/"}
                    >
                      {item.label}
                    </NavLink>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => { setMobileOpen(false); onOpenPalette && onOpenPalette(); }}
                className="block w-full text-left py-3 text-[14px] text-[#4A4A55]"
                data-testid="mobile-search-open"
              >
                <SearchIcon className="inline h-3.5 w-3.5 mr-2" />
                Search
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
